import { useState, useEffect } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";
import DOMPurify from "dompurify";
import { PenLine, Eye, ArrowLeft, ArrowRight, Clock, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import AttachmentList from "@/components/AttachmentList";

function BlogWrapper({ isAuth, children }) {
  if (isAuth) return <DashboardLayout>{children}</DashboardLayout>;
  return (
    <div className="min-h-screen bg-spa-bone">
      <header className="border-b border-spa-border bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-baseline gap-1">
            <span className="font-serif text-xl text-spa-moss font-medium">SMAHOO ME.WELL</span>
            <span className="font-serif text-xl text-spa-terracotta font-medium">Mind-SPA</span>
          </a>
          <Button onClick={() => window.location.href = "/"} className="spa-button-primary text-sm" data-testid="blog-signin-btn">Sign In</Button>
        </div>
      </header>
      {children}
    </div>
  );
}

export default function Blog() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.get('/blog');
        setPosts(res.data.posts);
        setIsAuth(res.data.is_authenticated);
      } catch (err) { /* silent */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const viewPost = async (slug) => {
    setViewLoading(true);
    try {
      const res = await apiClient.get(`/blog/${slug}`);
      setSelectedPost(res.data.post);
    } catch (err) { /* silent */ }
    finally { setViewLoading(false); }
  };

  if (selectedPost) {
    return (
      <BlogWrapper isAuth={!!user}>
        <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="blog-single">
          <button onClick={() => setSelectedPost(null)} className="flex items-center gap-2 text-spa-text-secondary hover:text-spa-moss mb-6 transition-colors" data-testid="blog-back">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </button>
          <article>
            <div className="flex items-center gap-2 mb-4">
              {selectedPost.tags?.map((t, i) => (
                <span key={`tag-${t}`} className="px-2 py-0.5 bg-spa-sage/10 text-spa-sage rounded-full text-xs flex items-center gap-1">
                  <Tag className="w-3 h-3" />{t}
                </span>
              ))}
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl font-medium text-spa-moss mb-4">{selectedPost.title}</h1>
            <div className="flex items-center gap-4 text-spa-text-secondary text-sm mb-8">
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{new Date(selectedPost.created_at).toLocaleDateString()}</span>
              <span>by {selectedPost.author_name}</span>
              <span className="flex items-center gap-1"><Eye className="w-4 h-4" />{selectedPost.views} views</span>
            </div>
            {selectedPost.content ? (
              <div className="prose prose-stone max-w-none text-spa-text-secondary" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedPost.content) }} />
            ) : (
              <div className="spa-card p-8 text-center">
                <p className="text-spa-text-secondary mb-4">{selectedPost.summary}</p>
                <p className="text-spa-moss font-medium mb-4">Sign in to read the full article</p>
                <Button className="spa-button-primary" onClick={() => window.location.href = "/"} data-testid="blog-cta-signin">Sign In / Register</Button>
              </div>
            )}
            <AttachmentList attachments={selectedPost.attachments} />
          </article>
        </div>
      </BlogWrapper>
    );
  }

  return (
    <BlogWrapper isAuth={!!user}>
      <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="blog-page">
        <div className="mb-8">
          <p className="overline-text mb-2">Mr. RAPsoul.R's</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-medium text-spa-moss mb-2">Rice And Peas For The Soul REVISITED</h1>
          <p className="text-spa-text-secondary">Wellness insights, stories, and guidance for your journey</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-spa-terracotta border-t-transparent rounded-full animate-spin" /></div>
        ) : posts.length > 0 ? (
          <div className="space-y-6">
            {posts.map(p => (
              <div key={p.post_id} className="spa-card group cursor-pointer hover:shadow-lg transition-shadow" onClick={() => viewPost(p.slug)} data-testid={`blog-card-${p.slug}`}>
                <div className="flex items-center gap-2 mb-3">
                  {p.tags?.slice(0, 3).map((t, i) => (
                    <span key={`tag-${t}`} className="px-2 py-0.5 bg-spa-sage/10 text-spa-sage rounded-full text-xs">{t}</span>
                  ))}
                  <span className="text-spa-text-secondary text-xs ml-auto">{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
                <h2 className="font-serif text-xl font-medium text-spa-moss mb-2 group-hover:text-spa-terracotta transition-colors">{p.title}</h2>
                <p className="text-spa-text-secondary text-sm mb-3">{p.summary}</p>
                <div className="flex items-center justify-between">
                  <span className="text-spa-text-secondary text-xs flex items-center gap-1"><Eye className="w-3 h-3" />{p.views || 0} views</span>
                  <span className="text-spa-terracotta text-sm font-medium flex items-center gap-1">
                    {isAuth ? "Read More" : "Sign in to read"} <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="spa-card p-12 text-center">
            <PenLine className="w-12 h-12 text-spa-border mx-auto mb-4" />
            <p className="text-spa-text-secondary">Blog posts coming soon</p>
          </div>
        )}
      </div>
    </BlogWrapper>
  );
}
